// Warrior talent metadata + 51-point build inference.
// Tree layout JSON is mirrored from wowsims/classic so the field names match
// what the sim's RaidSimRequest expects.

import warriorTreesJson from './warrior_talents.json';
import type { TalentBuild, TalentTreePoints } from './types';

export interface TalentNode {
  fieldName: string;            // matches wowsims WarriorTalents proto field (camelCase)
  treeIndex: 0 | 1 | 2;         // 0 = Arms, 1 = Fury, 2 = Protection
  treeName: 'Arms' | 'Fury' | 'Protection';
  rowIdx: number;               // 0..6, also = (tier requirement) / 5
  colIdx: number;               // 0..3
  maxPoints: number;
  spellIds: number[];           // 1 entry per rank
}

interface RawTalent {
  fieldName: string;
  location: { rowIdx: number; colIdx: number };
  spellIds: number[];
  maxPoints: number;
  prereqLocation?: { rowIdx: number; colIdx: number };
}

interface RawTree {
  name: 'Arms' | 'Fury' | 'Protection';
  backgroundUrl: string;
  talents: RawTalent[];
}

const RAW_TREES = warriorTreesJson as RawTree[];

export const TALENT_TREES: ReadonlyArray<{
  name: 'Arms' | 'Fury' | 'Protection';
  treeIndex: 0 | 1 | 2;
  nodes: TalentNode[];
}> = RAW_TREES.map((tree, i) => ({
  name: tree.name,
  treeIndex: i as 0 | 1 | 2,
  nodes: tree.talents.map((t) => ({
    fieldName: t.fieldName,
    treeIndex: i as 0 | 1 | 2,
    treeName: tree.name,
    rowIdx: t.location.rowIdx,
    colIdx: t.location.colIdx,
    maxPoints: t.maxPoints,
    spellIds: t.spellIds,
  })),
}));

const ALL_NODES: TalentNode[] = TALENT_TREES.flatMap((t) => t.nodes);

export function findTalent(fieldName: string): TalentNode | undefined {
  return ALL_NODES.find((n) => n.fieldName === fieldName);
}

// ---- Build inference ----
//
// Given total points spent in each tree, generate a plausible cookie-cutter
// allocation. The orderings below are picked for Naxx-tier fury (and standard
// arms/prot fallbacks). They satisfy tier requirements (each row 5*rowIdx
// total points) when filled in order.

type FillOrder = ReadonlyArray<readonly [string, number]>;

// RUOK guild standard 17/34/0 build, confirmed against the user's actual
// in-game allocation:
//   Arms 17 → IHS 3, Imp Rend 3, TM 5, Imp Overpower 1, Deep Wounds 3, Impale 2
//   Fury 34 → Cruelty 5, UW 5, Imp BS 2, Imp Cleave 3, DW Spec 5, Imp Execute 2,
//             Enrage 5, Death Wish 1, Flurry 5, Bloodthirst 1
const FURY_FILL_ORDER: FillOrder = [
  ['cruelty', 5],                      // Row 0  → cum  5
  ['unbridledWrath', 5],               // Row 1 (need 5)  → cum 10
  ['improvedBattleShout', 2],          // Row 2 (need 10, col 3) → cum 12
  ['improvedCleave', 3],               // Row 2 (col 0)  → cum 15
  ['dualWieldSpecialization', 5],      // Row 3 (need 15) → cum 20
  ['improvedExecute', 2],              // Row 3 → cum 22
  ['enrage', 5],                       // Row 3 → cum 27
  ['deathWish', 1],                    // Row 4 (need 20) → cum 28
  ['flurry', 5],                       // Row 5 (need 25, prereq enrage 5) → cum 33
  ['bloodthirst', 1],                  // Row 6 (need 30) → cum 34
  // Extras if user has > 34 fury (rare, e.g. arms cross-spec):
  ['boomingVoice', 5],
  ['improvedDemoralizingShout', 5],
  ['improvedSlam', 5],
];

const ARMS_FILL_ORDER: FillOrder = [
  ['improvedHeroicStrike', 3],         // Row 0  → cum  3
  ['improvedRend', 3],                 // Row 0  → cum  6
  ['tacticalMastery', 5],              // Row 1 (need 5)  → cum 11
  ['improvedOverpower', 1],            // Row 2 (need 10) → cum 12
  ['deepWounds', 3],                   // Row 2  → cum 15
  ['impale', 2],                       // Row 3 (need 15, prereq Deep Wounds) → cum 17
  // Extras for arms-main builds (~31):
  ['improvedCharge', 2],
  ['improvedThunderClap', 3],
  ['angerManagement', 1],
  ['twoHandedWeaponSpecialization', 5],
  ['swordSpecialization', 5],
  ['mortalStrike', 1],                 // Row 6 (need 30)
];

const PROT_FILL_ORDER: FillOrder = [
  ['shieldSpecialization', 5],
  ['anticipation', 5],
  ['toughness', 5],
  ['defiance', 5],
  ['improvedShieldBlock', 3],
  ['lastStand', 1],
  ['improvedRevenge', 3],
  ['improvedSunderArmor', 3],
  ['concussionBlow', 1],
  ['oneHandedWeaponSpecialization', 5],
  ['shieldSlam', 1],
];

function fillTree(out: TalentBuild, order: FillOrder, totalPts: number): void {
  let remaining = totalPts;
  for (const [fieldName, maxFromOrder] of order) {
    if (remaining <= 0) break;
    const node = findTalent(fieldName);
    if (!node) continue;
    const max = Math.min(maxFromOrder, node.maxPoints);
    const pts = Math.min(max, remaining);
    out[fieldName] = pts;
    remaining -= pts;
  }
}

export function inferTalentBuild(t: TalentTreePoints): TalentBuild {
  const build: TalentBuild = {};
  fillTree(build, ARMS_FILL_ORDER, t.arms);
  fillTree(build, FURY_FILL_ORDER, t.fury);
  fillTree(build, PROT_FILL_ORDER, t.protection);
  return build;
}

export function summarizeBuild(build: TalentBuild): TalentTreePoints {
  const out: TalentTreePoints = { arms: 0, fury: 0, protection: 0 };
  for (const [fieldName, pts] of Object.entries(build)) {
    if (!pts) continue;
    const node = findTalent(fieldName);
    if (!node) continue;
    if (node.treeIndex === 0) out.arms += pts;
    else if (node.treeIndex === 1) out.fury += pts;
    else out.protection += pts;
  }
  return out;
}

// ---- Validation: can we add / remove a point at this talent? ----
//
// Mirrors the rules in wowsims/classic talents_picker.tsx:
//   - You can't go past 51 total points across all 3 trees.
//   - Each row k (0-indexed) requires 5*k cumulative points already spent in
//     the same tree before any of its talents become available.
//   - A talent with prereqLocation needs that prereq talent at max points.
//   - Removing a point is blocked if doing so would break either rule for any
//     other allocated talent in the same tree.

const POINTS_PER_ROW = 5;
const MAX_TALENT_POINTS = 51;

interface TreeNodeWithPrereq extends TalentNode {
  prereqFieldName?: string;
}

const PREREQ_BY_FIELD = new Map<string, string>();
RAW_TREES.forEach((tree, i) => {
  tree.talents.forEach((t) => {
    if (!t.prereqLocation) return;
    const prereq = tree.talents.find(
      (other) => other.location.rowIdx === t.prereqLocation!.rowIdx && other.location.colIdx === t.prereqLocation!.colIdx,
    );
    if (prereq) PREREQ_BY_FIELD.set(t.fieldName, prereq.fieldName);
    void i;
  });
});

export function getPrereq(fieldName: string): TalentNode | undefined {
  const prereqField = PREREQ_BY_FIELD.get(fieldName);
  return prereqField ? findTalent(prereqField) : undefined;
}

export function totalPoints(build: TalentBuild): number {
  let n = 0;
  for (const v of Object.values(build)) n += v;
  return n;
}

function pointsInTree(build: TalentBuild, treeIndex: 0 | 1 | 2): number {
  let n = 0;
  for (const [field, pts] of Object.entries(build)) {
    const node = findTalent(field);
    if (node && node.treeIndex === treeIndex) n += pts;
  }
  return n;
}

export interface AllocCheck {
  ok: boolean;
  reason?: 'maxTotal' | 'rowReq' | 'prereq' | 'maxRank' | 'noPoints' | 'breaksRow' | 'breaksChild';
}

export function canAddPoint(node: TalentNode, build: TalentBuild): AllocCheck {
  const cur = build[node.fieldName] ?? 0;
  if (cur >= node.maxPoints) return { ok: false, reason: 'maxRank' };
  if (totalPoints(build) >= MAX_TALENT_POINTS) return { ok: false, reason: 'maxTotal' };
  const treePts = pointsInTree(build, node.treeIndex);
  if (treePts < node.rowIdx * POINTS_PER_ROW) return { ok: false, reason: 'rowReq' };
  const prereq = getPrereq(node.fieldName);
  if (prereq) {
    const prereqPts = build[prereq.fieldName] ?? 0;
    if (prereqPts < prereq.maxPoints) return { ok: false, reason: 'prereq' };
  }
  return { ok: true };
}

export function canRemovePoint(node: TalentNode, build: TalentBuild): AllocCheck {
  const cur = build[node.fieldName] ?? 0;
  if (cur <= 0) return { ok: false, reason: 'noPoints' };

  // Build the hypothetical post-remove allocation.
  const probe: TalentBuild = { ...build };
  probe[node.fieldName] = cur - 1;
  if (probe[node.fieldName] === 0) delete probe[node.fieldName];

  // Check that no allocated talent in the same tree loses its row requirement.
  const treeIdx = node.treeIndex;
  const tree = TALENT_TREES[treeIdx]!;
  const rowTotals: number[] = new Array(8).fill(0);
  for (const n of tree.nodes) {
    const pts = probe[n.fieldName] ?? 0;
    rowTotals[n.rowIdx] = (rowTotals[n.rowIdx] ?? 0) + pts;
  }
  const cumulative: number[] = [];
  let acc = 0;
  for (let i = 0; i < rowTotals.length; i++) {
    acc += rowTotals[i] ?? 0;
    cumulative[i] = acc;
  }
  for (const n of tree.nodes) {
    const pts = probe[n.fieldName] ?? 0;
    if (pts === 0) continue;
    if (n.rowIdx === 0) continue;
    const cumBeforeRow = cumulative[n.rowIdx - 1] ?? 0;
    if (cumBeforeRow < n.rowIdx * POINTS_PER_ROW) return { ok: false, reason: 'breaksRow' };
  }
  // Children that depend on this talent must not have any points if this drops below max.
  if ((probe[node.fieldName] ?? 0) < node.maxPoints) {
    for (const n of tree.nodes) {
      const prereq = getPrereq(n.fieldName);
      if (prereq && prereq.fieldName === node.fieldName) {
        if ((probe[n.fieldName] ?? 0) > 0) return { ok: false, reason: 'breaksChild' };
      }
    }
  }
  return { ok: true };
}

// Build a tree index: node by [row][col].
export function getTalentByLocation(treeIndex: 0 | 1 | 2, row: number, col: number): TalentNode | undefined {
  const tree = TALENT_TREES[treeIndex];
  if (!tree) return undefined;
  return tree.nodes.find((n) => n.rowIdx === row && n.colIdx === col);
}

export const TALENT_GRID_ROWS = 7;
export const TALENT_GRID_COLS = 4;

// Wowhead's tree-background image IDs (mirrored from warrior_talents.json).
// Routed through our /api/img reverse proxy so users in geo-blocked regions
// still get the images (zamimg directly refuses requests from those regions).
export const TREE_BACKGROUND_URLS: Record<'Arms' | 'Fury' | 'Protection', string> = {
  Arms: '/api/img/images/wow/talents/backgrounds/classic/161.jpg',
  Fury: '/api/img/images/wow/talents/backgrounds/classic/164.jpg',
  Protection: '/api/img/images/wow/talents/backgrounds/classic/163.jpg',
};

// Returns a list of (parent, child) pairs that need a connecting arrow drawn.
export interface PrereqPair {
  parent: TalentNode;
  child: TalentNode;
  treeIndex: 0 | 1 | 2;
}

export function listPrereqs(): PrereqPair[] {
  const out: PrereqPair[] = [];
  for (const tree of TALENT_TREES) {
    for (const node of tree.nodes) {
      const prereq = getPrereq(node.fieldName);
      if (prereq) out.push({ parent: prereq, child: node, treeIndex: tree.treeIndex });
    }
  }
  return out;
}

// Encodes a TalentBuild into the path string Wowhead's talent calculator uses,
// e.g. ".../talent-calc/warrior/30202050301030003500050200000050000000050000000".
// Each tree's talents are emitted in (rowIdx, colIdx) order; trailing zeros
// trimmed only at the very end.
export function encodeWowheadCalc(build: TalentBuild): string {
  const parts: string[] = [];
  for (const tree of TALENT_TREES) {
    const sorted = [...tree.nodes].sort((a, b) =>
      a.rowIdx !== b.rowIdx ? a.rowIdx - b.rowIdx : a.colIdx - b.colIdx,
    );
    let s = '';
    for (const node of sorted) s += String(build[node.fieldName] ?? 0);
    // trim trailing zeros within a tree only after the last allocated point
    s = s.replace(/0+$/, '');
    parts.push(s);
  }
  // Wowhead joins trees with no separator (each tree is fixed-positional and
  // has its own trailing-zero trim). We use '-' to be explicit; fall back to
  // raw concat if the calculator rejects '-'.
  return parts.join('-');
}
